import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ChevronLeft,
  ChevronRight,
  X,
  GraduationCap,
  FileText,
  ClipboardCheck,
  UsersRound,
  UserPlus,
  CalendarCheck,
  CalendarClock,
  BookOpen,
  Megaphone,
  Bell,
  LogOut,
  User,
  TrendingUp,
  NotebookText,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ADMIN_LOGIN_PATH } from '../../pages/auth/LoginPage';
import LOGO_URL from '../../assets/logo.jpeg';

const menuItems = [
  { path: '/faculty', icon: LayoutDashboard, label: 'Dashboard', exact: true, role: 'faculty' },
  { path: '/faculty/exams', icon: FileText, label: 'Exams', role: 'faculty' },
  { path: '/faculty/evaluations', icon: ClipboardCheck, label: 'Evaluations', role: 'faculty' },
  { path: '/faculty/attendance', icon: CalendarCheck, label: 'Attendance', role: 'faculty' },
  { path: '/faculty/classroom', icon: BookOpen, label: 'Classroom', role: 'faculty' },
  { path: '/faculty/schedule', icon: CalendarClock, label: 'Schedule', role: 'faculty' },
  { path: '/faculty/broadcast', icon: Megaphone, label: 'Broadcast', role: 'faculty' },
  { path: '/notifications', icon: Bell, label: 'Notifications', role: 'faculty' },
  { path: '/admin', icon: LayoutDashboard, label: 'Admin Dashboard', exact: true, role: 'admin' },
  { path: '/admin/students', icon: GraduationCap, label: 'Students', role: 'admin' },
  { path: '/admin/students/new', icon: UserPlus, label: 'Create Student', role: 'admin' },
  { path: '/admin/exams', icon: FileText, label: 'Exams', role: 'admin' },
  { path: '/admin/classroom', icon: BookOpen, label: 'Classroom', role: 'admin' },
  { path: '/admin/attendance', icon: CalendarCheck, label: 'Attendance', role: 'admin' },
  { path: '/admin/reports', icon: BarChart3, label: 'Reports', role: 'admin' },
  { path: '/admin/schedule', icon: CalendarClock, label: 'Schedules', role: 'admin' },
  { path: '/admin/groups', icon: UsersRound, label: 'Groups', role: 'admin' },
  { path: '/admin/broadcast', icon: Megaphone, label: 'Broadcast', role: 'admin' },
  { path: '/notifications', icon: Bell, label: 'Notifications', role: 'admin' },
  { path: '/admin/faculty', icon: Users, label: 'Faculty', role: 'admin' },
  { path: '/admin/faculty/create', icon: Users, label: 'Create Faculty', role: 'admin' },
  { path: '/student', icon: LayoutDashboard, label: 'Dashboard', exact: true, role: 'student' },
  { path: '/student/exams', icon: BookOpen, label: 'Exams', role: 'student' },
  { path: '/student/results', icon: FileText, label: 'My Results', role: 'student' },
  { path: '/student/classroom', icon: NotebookText, label: 'Classroom', role: 'student' },
  { path: '/student/progress', icon: TrendingUp, label: 'My Progress', role: 'student' },
  { path: '/student/profile', icon: User, label: 'Profile', role: 'student' },
  { path: '/notifications', icon: Bell, label: 'Notifications', role: 'student' },
];

export const Sidebar = ({
  isInline = true,
  isCollapsed = false,
  onToggleCollapsed,
  isMobileOpen = false,
  onCloseMobile,
}) => {
  // Icon-only mode is a desktop affordance; the drawer always shows labels.
  const collapsed = isInline && isCollapsed;
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const isStudent = user?.user_type === 'student';

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    // Same signal the nav-item filter below uses to pick the role's portal.
    const target =
      user?.user_type === 'admin'
        ? ADMIN_LOGIN_PATH
        : isStudent
        ? '/login/student'
        : '/login/faculty';
    logout();
    navigate(target);
  };

  return (
    <>
      {/* Backdrop: drawer mode only. */}
      {!isInline && isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/60"
          onClick={onCloseMobile}
          data-testid="sidebar-backdrop"
          aria-hidden="true"
        />
      )}

      <aside
        className={`h-screen bg-slate-900 text-slate-300 border-r border-slate-800 flex flex-col transition-all duration-300 ${
          isInline
            ? `shrink-0 ${collapsed ? 'w-20' : 'w-[var(--sidebar-w)]'}`
            : `fixed inset-y-0 left-0 z-50 w-[var(--sidebar-w)] ${
                isMobileOpen ? 'translate-x-0' : '-translate-x-full'
              }`
        }`}
        data-testid="sidebar"
        data-sidebar-mode={isInline ? 'inline' : 'drawer'}
        aria-hidden={!isInline && !isMobileOpen ? 'true' : undefined}
      >
        <div className="relative flex items-center justify-between p-4 border-b border-slate-800">
          <div className={`flex items-center gap-3 overflow-hidden ${collapsed ? 'justify-center w-full' : ''}`}>
            {collapsed ? (
              <GraduationCap className="w-8 h-8 text-accent-warm" />
            ) : (
              <>
                <img
                  src={LOGO_URL}
                  alt="Future Makers"
                  className="h-10 w-auto rounded bg-white p-1"
                />
              </>
            )}
          </div>
          {isInline ? (
            <button
              onClick={onToggleCollapsed}
              className={`p-1.5 rounded-md hover:bg-slate-800 transition-colors ${collapsed ? 'absolute right-2' : ''}`}
              data-testid="sidebar-toggle"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          ) : (
            <button
              onClick={onCloseMobile}
              className="p-1.5 rounded-md hover:bg-slate-800 transition-colors"
              data-testid="sidebar-close"
              aria-label="Close navigation"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* User info */}
        {!collapsed && isAuthenticated && user && (
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  isStudent ? 'bg-primary/20 text-primary' : 'bg-secondary/20 text-secondary'
                }`}
              >
                {user?.name?.charAt(0).toUpperCase() || (isStudent ? 'S' : 'F')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
          <nav className="py-4">
            <ul className="space-y-1">
              {menuItems
                .filter((item) => item.role === user?.user_type)
                .map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={isInline ? undefined : onCloseMobile}
                      className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-md transition-colors ${
                        isActive(item.path, item.exact)
                          ? 'bg-primary text-white font-medium'
                          : 'hover:bg-slate-800 hover:text-white'
                      } ${collapsed ? 'justify-center' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon size={20} />
                      {!collapsed && <span>{item.label}</span>}
                    </Link>
                  </li>
                ))}
            </ul>
          </nav>

          <div className={`mt-auto p-4 border-t border-slate-800 ${collapsed ? 'text-center' : ''}`}>
            {isAuthenticated && (
              <button
                onClick={handleLogout}
                className={`flex items-center gap-3 w-full min-h-11 px-4 py-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800 transition-colors ${
                  collapsed ? 'justify-center' : ''
                }`}
              >
                <LogOut size={20} />
                {!collapsed && <span>Logout</span>}
              </button>
            )}
            {!collapsed && (
              <p
                className={`text-xs text-slate-500 mt-3 pt-3 border-t border-slate-800/70 ${
                  isStudent ? 'text-center' : ''
                }`}
              >
                Future Makers v2.0 - {isStudent ? 'Student' : 'Faculty'}
              </p>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};
